import { type PropsWithChildren, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPermissions } from '@/domains/user/enums/UserPermissions';
import { selectUser, selectUserPermissions } from '@/domains/user/store/selectors';
import { useSelector } from '@/store/hooks';

type PermissionGuardProps = PropsWithChildren<{
  permissions?: UserPermissions | UserPermissions[];
  invert?: boolean;
  exact?: boolean;
  redirectTo?: string;
}>;

export const PermissionGuard = ({
  children,
  invert = false,
  permissions = [],
  exact = true,
  redirectTo,
}: PermissionGuardProps) => {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const userPermissions = useSelector(selectUserPermissions);
  const permissionArray = Array.isArray(permissions) ? permissions : [permissions];

  const permissionAllowed = exact
    ? permissionArray.every((permission) => userPermissions.includes(permission))
    : permissionArray.some((permission) => userPermissions.includes(permission));

  let hasPermission = permissionAllowed;

  if (invert) hasPermission = !hasPermission;

  useEffect(() => {
    if (!hasPermission && user && redirectTo) {
      navigate(redirectTo, { replace: true });
    }
  }, [hasPermission, user, redirectTo, navigate]);

  if (!hasPermission) return null;

  return <>{children}</>;
};
